import { useState, useEffect } from 'react';
import { developerApi, versionApi } from '../../services/api';
import { formatNumber, formatMs, formatDateTime, getRiskStatusConfig, getDeploymentConfig, formatVersion } from '../../utils/formatters';

/**
 * VersionDrilldownPanel — Slide-in panel for version deep-dive.
 * Shows: Top Crashes, ANR Breakdown, Device Distribution, Deployment Recommendation.
 * Progressive disclosure: opens on row click from VersionIntelligenceTable.
 */

const TABS = [
  { key: 'crashes', label: 'Top Crashes' },
  { key: 'anr', label: 'ANR' },
  { key: 'devices', label: 'Devices' },
  { key: 'recommendation', label: 'Decision' },
];

function MiniCrashRow({ crash }) {
  return (
    <div className="flex items-center gap-3 py-2 px-1 border-b border-surface-700/50 last:border-0">
      <span className="text-red-400 font-mono text-xs font-bold shrink-0 w-12 text-right">
        {formatNumber(crash.crash_count)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-200 truncate">{crash.title}</p>
        <p className="text-[10px] text-gray-500 truncate">{crash.subtitle}</p>
      </div>
      <span className="text-[10px] text-gray-600 shrink-0">
        {crash.user_count} users
      </span>
    </div>
  );
}

function DeviceRow({ device, maxCount }) {
  const pct = maxCount > 0 ? (device.count / maxCount) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-xs text-gray-300 w-32 truncate">{device.model || device.device}</span>
      <div className="flex-1 h-2 bg-surface-700 rounded-full overflow-hidden">
        <div className="h-full bg-brand-500/60 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-gray-500 w-12 text-right">{device.count}</span>
    </div>
  );
}

export default function VersionDrilldownPanel({ version, onClose }) {
  const [activeTab, setActiveTab] = useState('crashes');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ crashes: [], anr: {}, devices: [], decision: null });

  const ver = version?.app_version || version?.appVersion;

  useEffect(() => {
    if (!ver) return;
    setLoading(true);
    setActiveTab('crashes');

    const load = async () => {
      try {
        const [crashRes, anrRes, deviceRes, decisionRes] = await Promise.allSettled([
          developerApi.getCrashes({ appVersion: ver, limit: 10 }),
          developerApi.getAnr({ appVersion: ver }),
          developerApi.getDevices({ appVersion: ver }),
          developerApi.getVersionRisk(ver),
        ]);

        setData({
          crashes: crashRes.status === 'fulfilled' ? (crashRes.value.data.crashes || []) : [],
          anr: anrRes.status === 'fulfilled' ? (anrRes.value.data || {}) : {},
          devices: deviceRes.status === 'fulfilled' ? (deviceRes.value.data.devices || []) : [],
          decision: decisionRes.status === 'fulfilled' ? decisionRes.value.data : null,
        });
      } catch (err) {
        console.error('Drilldown load failed:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [ver]);

  if (!version) return null;

  const riskConfig = getRiskStatusConfig(version.risk_status);
  const deployConfig = data.decision?.deploymentRecommendation
    ? getDeploymentConfig(data.decision.deploymentRecommendation)
    : null;

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] bg-surface-800 border-l border-surface-600 shadow-2xl z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-600">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-surface-700 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div>
            <h3 className="text-sm font-bold text-gray-100">
              {formatVersion(ver, version.version_code || version.versionCode)}
            </h3>
            <p className="text-[10px] text-gray-500">
              {version.release_date ? `Released ${new Date(version.release_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : 'Version Details'}
            </p>
          </div>
        </div>
        {riskConfig && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${riskConfig.bg} ${riskConfig.color}`}>
            {riskConfig.label}
          </span>
        )}
      </div>

      {/* Quick stats bar */}
      <div className="grid grid-cols-4 gap-px bg-surface-600/30 border-b border-surface-600">
        {[
          { label: 'Risk', value: version.release_risk_score != null ? Math.round(version.release_risk_score) : '—' },
          { label: 'Crash-Free', value: version.crash_free_pct != null ? `${parseFloat(version.crash_free_pct).toFixed(1)}%` : '—' },
          { label: 'ANR', value: version.anr_rate != null ? `${parseFloat(version.anr_rate).toFixed(3)}%` : '—' },
          { label: 'Users', value: version.total_users ? formatNumber(version.total_users) : '—' },
        ].map(s => (
          <div key={s.label} className="bg-surface-800 px-3 py-2 text-center">
            <p className="text-[10px] text-gray-500 uppercase">{s.label}</p>
            <p className="text-sm font-bold text-gray-200">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-3 pt-2 pb-1 border-b border-surface-600/50">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-surface-600 text-gray-100'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'crashes' && (
              <div>
                {data.crashes.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-6">No crash data for this version</p>
                ) : (
                  data.crashes.slice(0, 10).map((c, i) => <MiniCrashRow key={c.id || i} crash={c} />)
                )}
              </div>
            )}

            {activeTab === 'anr' && (
              <div className="space-y-4">
                {data.anr.byType?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">By Type</h4>
                    {data.anr.byType.map((a, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-surface-700/50 last:border-0">
                        <span className="text-sm text-gray-300">{a.type || a.anr_type}</span>
                        <span className="text-xs font-mono text-amber-400">{formatNumber(a.count || a.anr_count)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {data.anr.byOs?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">By OS Version</h4>
                    {data.anr.byOs.slice(0, 8).map((a, i) => (
                      <div key={i} className="flex items-center justify-between py-1 text-xs">
                        <span className="text-gray-300">{a.os_version}</span>
                        <span className="text-gray-500">{formatNumber(a.count || a.anr_count)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(!data.anr.byType?.length && !data.anr.byOs?.length) && (
                  <p className="text-gray-500 text-sm text-center py-6">No ANR data for this version</p>
                )}
              </div>
            )}

            {activeTab === 'devices' && (
              <div>
                {data.devices.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-6">No device data for this version</p>
                ) : (
                  <>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase mb-3">Device Distribution</h4>
                    {data.devices.slice(0, 15).map((d, i) => (
                      <DeviceRow key={i} device={d} maxCount={data.devices[0]?.count || 1} />
                    ))}
                  </>
                )}
              </div>
            )}

            {activeTab === 'recommendation' && (
              <div className="space-y-4">
                {deployConfig ? (
                  <>
                    <div className={`rounded-xl p-4 border-l-4 ${
                      data.decision.deploymentRecommendation === 'continue' ? 'bg-emerald-500/10 border-emerald-500' :
                      data.decision.deploymentRecommendation === 'pause' ? 'bg-amber-500/10 border-amber-500' :
                      'bg-red-500/10 border-red-500'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-lg font-bold ${deployConfig.color}`}>
                          {deployConfig.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300">
                        {data.decision.deploymentRecommendation === 'continue' && 'This release is performing well. Safe to continue rollout.'}
                        {data.decision.deploymentRecommendation === 'pause' && 'Potential issues detected. Consider pausing rollout for investigation.'}
                        {data.decision.deploymentRecommendation === 'rollback' && 'Critical issues detected. Rollback is recommended.'}
                      </p>
                    </div>

                    {data.decision.recommendationReasons?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Reasons</h4>
                        <ul className="space-y-1.5">
                          {data.decision.recommendationReasons.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                              <span className="text-gray-500 mt-0.5">•</span>
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {data.decision.componentScores && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Component Scores</h4>
                        <div className="space-y-2">
                          {Object.entries(data.decision.componentScores).map(([key, val]) => (
                            <div key={key} className="flex items-center gap-2">
                              <span className="text-xs text-gray-400 w-24 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                              <div className="flex-1 h-1.5 bg-surface-700 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${val >= 75 ? 'bg-emerald-500' : val >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                  style={{ width: `${Math.min(val, 100)}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-gray-500 w-8 text-right">{Math.round(val)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-6">No deployment recommendation available</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
